import { assign } from 'xstate';

import { CareType, updateMatchPreferences } from '@uplift/api';
import { STUDENT_ONBOARDING_ROUTING_CONFIG } from '@uplift/constants';
import { ProviderFilterTypeEnum, ProviderMedicalRole } from '@uplift/types';

import { StudentOnboardingMachineContext } from '../types';
import { studentMachineSetup } from './setup';
import { eligibilityGuard, MATCHES_LOADING_DURATION, syncRouteInvocation } from './utils';

const initialContext: StudentOnboardingMachineContext = {
  orgId: '',
  orgContract: null,
  isSlotUnavailable: false,
  isLoggedIn: null,
  phoneVerified: false,
  userId: '',
  matchId: null,
  seed: null,
  stateId: null,
  insuranceId: null,
  modalityId: null,
  age: null,
  specialtyIds: [],

  matchingPaymentType: null,
  isPaymentInfoSubmitted: false,

  careType: CareType.therapy,
  medicalRole: ProviderMedicalRole.therapist,
  isStudentSecondBooking: false,

  showTheseProviderFilters: [],
  identityFilters: {
    genderIds: [],
    faithIds: [],
    ethnicityIds: [],
  },
  experienceFilters: {
    communityIds: [],
    faithIds: [],
    ethnicityIds: [],
  },
  availabilityTimeOfDay: [],
  availabilityDayOfWeek: [],

  termsAccepted: false,

  selectedProviderId: null,
  appointment: null,
  appointmentProviderId: null,
  referrer: null,
  inviteToken: null,
  providerUrl: null,

  secondProviderId: null,
  secondAppointment: null,
  progressPercentage: 0,

  intakeFormSubmissionId: null,
};


const syncContextDataToDatabase = async ({ context }: { context: StudentOnboardingMachineContext }) => {
  if (context.isLoggedIn && context.matchId) {
    await updateMatchPreferences(context.matchId, {
      stateId: context.stateId,
      visitReasonIds: context.specialtyIds,
      insuranceCompanyId: context.insuranceId,
      modalityId: context.modalityId,
      ageGroupId: context.age?.id,
      faithIds: context.identityFilters.faithIds,
      ethnicityIds: context.identityFilters.ethnicityIds,
      genderIds: context.identityFilters.genderIds,
      communityExpertise: {
        faithIds: context.experienceFilters.faithIds,
        ethnicityIds: context.experienceFilters.ethnicityIds,
        generalIds: context.experienceFilters.communityIds,
      },
      directFlowData: context.providerUrl ? {
        providerId: context.selectedProviderId,
        appointment: context.appointment.fromDatetime,
        publicProfileUrl: context.providerUrl,
      } : null
    });
  }
};

export const studentOnboardingMachine = studentMachineSetup.createMachine({
  id: 'student-matching',
  initial: 'Idle',
  invoke: { src: 'handleHistoryPop' },
  context: (): StudentOnboardingMachineContext => initialContext,
  states: {
    Idle: {
      on: {
        Start: 'Initial'
      }
    },
    Initial: {
      invoke: {
        src: 'load',
        onError: {
          target: 'InvalidLink'
        },
        onDone: [
          {
            target: 'SelectStep',
            actions: assign(({ event }) => event.output),
          },
        ],
      }
    },

    InvalidLink: {
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.invalidLink),
    },

    SelectStep: {
      always: [
        {
          target: 'Welcome',
          guard: ({ context }) => !context.isLoggedIn,
        },
        {
          target: 'CreateAccount',
          guard: ({ context }) => !context.isLoggedIn,
        },
        {
          target: 'VerifyPhone',
          guard: ({ context }) => (
            (context.isLoggedIn
            && context.phoneVerified === false && !context.stateId) // skip VerifyPhone screen when student skipped verification and selected state
          ),
        },
        {
          target: 'Where',
          guard: ({ context }) => (context.isLoggedIn && !context.stateId),
        },
        {
          target: 'Insurance',
          guard: ({ context }) => (context.isLoggedIn && !context.insuranceId)
        },
        {
          target: 'Age',
          guard: ({ context }) => (context.isLoggedIn && !context.age),
        },
        {
          target: 'Reason',
          guard: ({ context }) => (context.isLoggedIn && !context.specialtyIds.length),
        },
        {
          target: 'MatchesLoading',
          guard: ({ context }) => context.isLoggedIn,
        },
        {
          target: 'BookingSecondInterstitial',
          guard: ({ context }) => context.termsAccepted,
        },
      ],
    },

    Welcome: {
      entry: assign({ progressPercentage: 0 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.welcome),
      on: {
        'Welcome.Next': 'CreateAccount'
      }
    },
    CreateAccount: {
      entry: assign({ progressPercentage: 0 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.createAccount),
      on: {
        'CreateAccount.Next': 'LoadAfterAccountCreation',
        Back: 'Welcome'
      }
    },
    /**
     * This state is used to load the context data after the account creation (org contract, org id, etc.)
     */
    LoadAfterAccountCreation: {
      invoke: {
        src: 'load',
        onDone: {
          target: 'VerifyPhone',
          actions: assign(({ event }) => event.output)
        },
      }
    },
    VerifyPhone: {
      entry: assign({ progressPercentage: 5 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.verifyPhone),
      on: {
        'VerifyPhone.Next': [
          {
            target: 'VerifyPhoneCode',
            guard: ({ event }) => event.data.contactVerificationInitiated,
          },
          {
            target: 'Where',
          }
        ],
        Back: 'CreateAccount'
      }
    },
    VerifyPhoneCode: {
      entry: assign({ progressPercentage: 5 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.verifyPhoneCode),
      on: {
        'VerifyPhoneCode.Next': {
          target: 'Where',
          actions: [
            assign({
              phoneVerified: ({ event }) => event.data.phoneVerified
            }),
          ]
        },
        Back: 'VerifyPhone'
      }
    },
    Where: {
      entry: assign({ progressPercentage: 10 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.where),
      on: {
        'Where.Next': {
          target: 'Insurance',
          actions: [
            assign({
              stateId: ({ event }) => event.data.stateId,
            }),
            syncContextDataToDatabase,
          ]
        },
        'Where.NotAvailable': {
          target: 'NotAvailable',
          actions: [
            assign({
              stateId: ({ event }) => event.data.stateId,
            }),
            syncContextDataToDatabase
          ]
        },
        Back: 'VerifyPhone'
      },
    },
    NotAvailable: {
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.notAvailable),
      on: {
        Back: 'Where'
      }
    },
    Insurance: {
      entry: assign({ progressPercentage: 20 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.insurance),
      on: {
        'Insurance.Next': [
          {
            actions: [
              assign({
                insuranceId: ({ event }) => event.data.insuranceId,
              }),
              syncContextDataToDatabase
            ],
            target: 'BookingFirstInterstitial',
          },
        ],
        Back: 'Where'
      }
    },
    BookingFirstInterstitial: {
      entry: assign({
        progressPercentage: 25,
        medicalRole: eligibilityGuard(({ psychFirst }) => psychFirst ? ProviderMedicalRole.psychiatrist : ProviderMedicalRole.therapist),
      }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.bookingFirstInterstitial, { replace: true }),
      always: [
        {
          target: 'Age',
          guard: eligibilityGuard(({ isPlanLite }) => isPlanLite),
        },
      ],
      on: {
        'BookingFirstInterstitial.Next': [
          { target: 'Age' }
        ],
        Back: 'Insurance'
      },
    },
    Age: {
      entry: assign({ progressPercentage: 35 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.age),
      on: {
        'Age.Next': [
          {
            target: 'TooYoung',
            guard: {
              type: 'isTooYoung',
              params: ({ event }) => ({ age: event.data.age })
            }
          },
          {
            target: 'Reason',
            actions: [
              assign({ age: ({ event }) => event.data.age }),
              syncContextDataToDatabase,
            ]
          }
        ],
        Back: {
          target: 'Insurance',
          actions: assign({ age: null })
        }
      }
    },
    TooYoung: {
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.tooYoung),
      on: { Back: 'Age' }
    },
    Reason: {
      entry: assign({ progressPercentage: 40 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.reason),
      on: {
        'Reason.Next': [
          {
            target: 'ProviderFilters',
            actions: [
              assign({ specialtyIds: ({ event }) => event.data.specialtyIds }),
              syncContextDataToDatabase,
            ],
          }
        ],
        Back: 'Age'
      }
    },
    ProviderFilters: {
      entry: assign({ progressPercentage: 60.5 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.providerFilters),
      always: [
        {
          target: 'MatchesLoading',
          guard: eligibilityGuard(({ psychFirst }) => psychFirst),
        },
      ],
      on: {
        'ProviderFilters.Next': {
          target: 'ProviderIdentityFilter',
          actions: assign({ showTheseProviderFilters: ({ event }) => event.data.providerFilters })
        },
        Back: 'Reason'
      }
    },
    ProviderIdentityFilter: {
      entry: assign({ progressPercentage: 62 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.providerIdentityFilter),
      always: [
        {
          target: 'ProviderExperienceFilter',
          guard: ({ context }) => !context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Identity),
        },
      ],
      on: {
        'ProviderIdentityFilter.Next': {
          target: 'ProviderExperienceFilter',
          actions: [
            assign({ identityFilters: ({ event }) => event.data }),
            syncContextDataToDatabase,
          ]
        },
        Back: 'ProviderFilters'
      }
    },
    ProviderExperienceFilter: {
      entry: assign({ progressPercentage: 63 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.providerExperienceFilter),
      always: [
        {
          target: 'MatchesLoading',
          guard: ({ context }) => !context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Experience),
        },
      ],
      on: {
        'ProviderExperienceFilter.Next': {
          target: 'MatchesLoading',
          actions: [
            assign({ experienceFilters: ({ event }) => event.data }),
            syncContextDataToDatabase,
          ]
        },
        Back: [
          {
            target: 'ProviderIdentityFilter',
            guard: ({ context }) => context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Identity),
          },
          'ProviderFilters'
        ]
      }
    },
    MatchesLoading: {
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.matchesLoading),
      after: {
        [MATCHES_LOADING_DURATION]: [
          { target: 'Matches' },
        ]
      },
      on: {
        Back: [
          {
            target: 'Reason',
            guard:  eligibilityGuard(({ psychFirst }) => psychFirst),
          },
          'ProviderFilters'
        ]
      }
    },
    Matches: {
      entry: assign({
        progressPercentage: 65,
        medicalRole: eligibilityGuard(({ psychFirst }) => psychFirst ? ProviderMedicalRole.psychiatrist : ProviderMedicalRole.therapist),
      }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.matches),
      on: {
        Back: [
          {
            target: 'Reason',
            guard: ({ context }) => context.careType === CareType.psychiatry,
          },
          {
            target: 'ProviderExperienceFilter',
            guard: ({ context }) => context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Experience),
          },
          {
            target: 'ProviderIdentityFilter',
            guard: ({ context }) => context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Identity),
          },
          'ProviderFilters'
        ],
        'Matches.UpdateFilters': {
          actions: [
            assign({
              insuranceId: ({ event }) => event.data.insuranceId,
              specialtyIds: ({ event }) => event.data.specialtyIds,
              identityFilters: ({ event }) => event.data.identityFilters,
              experienceFilters: ({ event }) => event.data.experienceFilters,
              availabilityTimeOfDay: ({ event }) => event.data.availabilityTimeOfDay,
              availabilityDayOfWeek: ({ event }) => event.data.availabilityDayOfWeek,
            }),
            syncContextDataToDatabase,
          ]
        },
        'Matches.SelectProvider': {
          target: 'Match',
          actions: assign({
            selectedProviderId: ({ event }) => event.data.providerId,
            matchingPaymentType: ({ event }) => event.data.matchingPaymentType,
          }),
        }
      }
    },
    Match: {
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.match),
      on: {
        'Match.ScheduleSession': {
          target: 'AboutYou',
          actions: assign({ appointment: ({ event }) => event.data.appointment })
        },
        Back: {
          target: 'Matches',
          actions: assign({ selectedProviderId: null })
        }
      }
    },
    AboutYou: {
      entry: assign({ progressPercentage: 70 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.aboutYou),
      on: {
        'AboutYou.Next': 'InsuranceInfo',
        Back: 'Match'
      }
    },
    InsuranceInfo: {
      entry: assign({ progressPercentage: 75 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.insuranceInfo),
      // If the user is self-pay, they skip the insurance info screen
      always: [
        {
          guard: eligibilityGuard(({ hasInsurance }) => !hasInsurance),
          target: 'FirstBookingPaymentInfo'
        },
      ],
      on: {
        'InsuranceInfo.Next': [
          {
            target: 'FirstBookingPaymentInfo',
            actions: [
              assign({ insuranceId: ({ event }) => event.data.insuranceId }),
              syncContextDataToDatabase,
            ]
          },
        ],
        Back: 'AboutYou'
      }
    },
    FirstBookingPaymentInfo: {
      entry: assign({ progressPercentage: 80 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.paymentInfo),
      // If school is sponsoring this care type, student will skip the payment info screen
      always: [
        {
          target: 'ConfirmBooking',
          guard: 'isInsuranceMedicaid',
        },
        {
          target: 'ConfirmBooking',
          guard: eligibilityGuard(({ isBookingSponsored }) => isBookingSponsored),
        }
      ],
      on: {
        Back: [
          {
            guard: eligibilityGuard(({ hasInsurance }) => !hasInsurance),
            target: 'AboutYou',
          },
          'InsuranceInfo'
        ],
        'PaymentInfo.Next': [
          {
            target: 'ConfirmBooking',
            actions: [
              assign({ isPaymentInfoSubmitted: true }),
              syncContextDataToDatabase,
            ]
          },
        ],
      }
    },
    ConfirmBooking: {
      entry: assign({ progressPercentage: 85 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.confirmBooking),
      on: {
        Back: [
          {
            target: 'InsuranceInfo',
            guard: 'isInsuranceMedicaid',
          },
          {
            target: 'InsuranceInfo',
            guard: eligibilityGuard(({ isBookingSponsored, hasInsurance }) => isBookingSponsored && hasInsurance),
          },
          {
            target: 'AboutYou',
            guard: eligibilityGuard(({ isBookingSponsored, hasInsurance }) => isBookingSponsored || !hasInsurance),
          },
          {
            target: 'FirstBookingPaymentInfo'
          },
        ],
        'ConfirmBooking.Next': [
          {
            target: 'BookingSecondInterstitial',
            actions: [
              assign({
                termsAccepted: true,
                intakeFormSubmissionId: ({ event }) => event.data.intakeFormSubmissionId
              }),
              syncContextDataToDatabase,
            ]
          }
        ],
        'ConfirmBooking.BookingConflict': {
          target: 'Match',
          actions: assign({ isSlotUnavailable: true }),
        },
      }
    },
    BookingSecondInterstitial: {
      entry: assign({
        progressPercentage: 90,
        isSlotUnavailable: false,
        medicalRole: eligibilityGuard(({ psychFirst }) => psychFirst ? ProviderMedicalRole.therapist : ProviderMedicalRole.psychiatrist),
        isStudentSecondBooking: true,
      }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.bookingSecondInterstitial, { replace: true }),
      // If the student only requires one care type, they skip the second booking
      always: [
        {
          target: 'BookingSuccess',
          guard: eligibilityGuard(({ referredForBoth }) => !referredForBoth),
        }
      ],
      on: {
        'BookingSecondInterstitial.Next': 'SecondProviderFilters',
      }
    },
    SecondProviderFilters: {
      entry: assign({ progressPercentage: 91 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.providerFilters),
      always: [
        {
          target: 'SecondMatches',
          guard: ({ context }) => context.medicalRole !== ProviderMedicalRole.therapist,
        },
      ],
      on: {
        'ProviderFilters.Next': {
          target: 'SecondProviderIdentityFilter',
          actions: assign({ showTheseProviderFilters: ({ event }) => event.data.providerFilters })
        },
        Back: 'BookingSecondInterstitial'
      }
    },
    SecondProviderIdentityFilter: {
      entry: assign({ progressPercentage: 92 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.providerIdentityFilter),
      always: [
        {
          target: 'SecondProviderExperienceFilter',
          guard: ({ context }) => !context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Identity),
        },
      ],
      on: {
        'ProviderIdentityFilter.Next': {
          target: 'SecondProviderExperienceFilter',
          actions: [
            assign({ identityFilters: ({ event }) => event.data }),
            syncContextDataToDatabase,
          ]
        },
        Back: 'SecondProviderFilters'
      }
    },
    SecondProviderExperienceFilter: {
      entry: assign({ progressPercentage: 93 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.providerExperienceFilter),
      always: [
        {
          target: 'SecondMatches',
          guard: ({ context }) => !context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Experience),
        },
      ],
      on: {
        'ProviderExperienceFilter.Next': {
          target: 'SecondMatches',
          actions: [
            assign({ experienceFilters: ({ event }) => event.data }),
            syncContextDataToDatabase,
          ]
        },
        Back: [
          {
            target: 'SecondProviderIdentityFilter',
            guard: ({ context }) => context.showTheseProviderFilters.includes(ProviderFilterTypeEnum.Identity),
          },
          'SecondProviderFilters'
        ]
      }
    },

    SecondMatches: {
      entry: assign({ progressPercentage: 94 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.secondMatches),
      on: {
        'Matches.UpdateFilters': {
          actions: [
            syncContextDataToDatabase,
          ]
        },
        'Matches.SelectProvider': {
          target: 'SecondMatch',
          actions: assign({ secondProviderId: ({ event }) => event.data.providerId }),
        },
        'Back': 'BookingSecondInterstitial'
      }
    },
    SecondMatch: {
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.secondMatch),
      on: {
        'Match.ScheduleSession': {
          target: 'SecondBooking',
          actions: assign({ secondAppointment: ({ event }) => event.data.appointment })
        },
        'Back': 'SecondMatches'
      }
    },
    SecondBooking: {
      tags: 'loading',
      invoke: {
        src: 'bookingSecond',
        input: ({ context }) => ({
          medicalRole: context.medicalRole,
          appointment: context.secondAppointment,
          providerId: context.secondProviderId,
          userId: context.userId,
        }),
        onDone: 'SecondBookingPaymentInfo',
        onError: {
          target: 'SecondMatches',
          actions: assign({ isSlotUnavailable: true }),
        }
      }
    },
    SecondBookingPaymentInfo: {
      entry: assign({ progressPercentage: 95, isSlotUnavailable: false }),
      always: [
        {
          target: 'BookingSuccess',
          guard: 'isInsuranceMedicaid',
        },
        // If school is sponsoring second booking or student already added payment info during first booking,
        // student will skip the payment info screen
        {
          target: 'BookingSuccess',
          guard: eligibilityGuard(({ isBookingSponsored }, context) => context.isPaymentInfoSubmitted || isBookingSponsored),
        }
      ],
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.paymentInfo),
      on: {
        'PaymentInfo.Next': 'BookingSuccess',
      },
    },

    BookingSuccess: {
      entry: assign({ progressPercentage: 100 }),
      invoke: syncRouteInvocation(STUDENT_ONBOARDING_ROUTING_CONFIG.bookingSuccess),
    }
  }
});
